/**
 * fibonacci.ts — mathematical layout engine for the Pulse.
 *
 * The user said: "Let's build it so the mathematics builds the page where it
 * uses the Fibonacci sequence to understand what is best to give the user
 * for that screen size."
 *
 * This module is pure math — no DOM, no state. It picks ratios, sizes,
 * and grid templates based on:
 *
 *   - The Fibonacci sequence: 1, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144, 233
 *   - The golden ratio: φ = (1 + √5) / 2 ≈ 1.6180339887…
 *   - The viewport (width × height × dpr)
 *   - The content type (chat / video / gallery / text / data / split / canvas)
 *
 * Every function is deterministic — same viewport + same content type
 * gives the same layout. The Pulse always ticks at 1.287 Hz; layouts
 * are stable, never jittery.
 */

/** Classical golden ratio. */
export const PHI = (1 + Math.sqrt(5)) / 2;

/** Reciprocal of φ — useful for "minor" splits (≈ 0.618). */
export const PHI_INV = 1 / PHI;

/** First 13 Fibonacci numbers — covers screens up to ~233 visual units. */
export const FIB: readonly number[] = [1, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144, 233];

/** Successive Fibonacci ratios converge on φ. Pick a clean integer ratio. */
const RATIOS: ReadonlyArray<readonly [number, number]> = [
  [1, 1],   // 1.000
  [1, 2],   // 0.500
  [2, 3],   // 0.667
  [3, 5],   // 0.600  → ≈ 1/φ
  [5, 8],   // 0.625
  [8, 13],  // 0.615  → ≈ 1/φ
  [13, 21], // 0.619  → ≈ 1/φ
  [21, 34], // 0.617
];

export interface Viewport {
  w: number;
  h: number;
  dpr?: number;
}

export type ContentType =
  | "chat"     // long messages, composer, reading mode
  | "video"    // single hero panel, no chrome
  | "gallery"  // image grid, masonry
  | "text"     // long-form reading
  | "data"     // table / dashboard
  | "split"    // input + live output
  | "canvas";  // freeform — agent picks

export type Orientation = "portrait" | "landscape" | "square";

/** Classify the viewport's orientation. Square zone uses ±5% of φ. */
export function orientation(vp: Viewport): Orientation {
  const r = vp.w / vp.h;
  if (r > 1.05) return "landscape";
  if (r < 0.95) return "portrait";
  return "square";
}

/**
 * Pick the closest Fibonacci ratio to a target. Used when the content
 * implies a specific proportion (e.g. video → 16:9 ≈ 1.778; gallery
 * thumbnail → 1:1 → 1, etc.) and we want to round to a clean φ-adjacent
 * Fibonacci pair.
 */
export function fibRatio(targetRatio: number): readonly [number, number] {
  let best = RATIOS[0];
  let bestDelta = Infinity;
  for (const r of RATIOS) {
    const value = r[0] / r[1];
    const inv = r[1] / r[0];
    const delta = Math.min(Math.abs(value - targetRatio), Math.abs(inv - targetRatio));
    if (delta < bestDelta) { bestDelta = delta; best = r; }
  }
  return best;
}

/**
 * Modular type scale rooted at `base` and stepped by φ. Returns N rems.
 *
 * Example output for base=16, steps=6:
 *   [16, 16φ, 16φ², 16φ³, 16φ⁴, 16φ⁵]
 *   = [16, 25.89, 41.89, 67.78, 109.66, 177.44]
 *
 * Caller picks indices (0=body, 1=lead, 2=h3, 3=h2, 4=h1, 5=display).
 */
export function typeScale(base = 16, steps = 6): number[] {
  const out: number[] = [];
  for (let i = 0; i < steps; i++) out.push(base * Math.pow(PHI, i));
  return out;
}

/**
 * "Reading width" — the column width that minimises eye-saccade cost
 * for body text. Hard-bounded between 320 and the larger of vp.w / φ
 * (golden width) and 720 (the Bringhurst recommendation).
 */
export function readingWidth(vp: Viewport): number {
  const golden = vp.w / PHI;
  const cap = Math.min(720, vp.w * 0.82);
  return Math.max(320, Math.min(golden, cap));
}

/** Spacing scale rooted at `base`, stepped by φ. Index 0 = base. */
export function spaceScale(base = 8, steps = 7): number[] {
  const out: number[] = [];
  for (let i = 0; i < steps; i++) out.push(Math.round(base * Math.pow(PHI, i)));
  return out;
}

// ─── Layout pickers per content type ──────────────────────────────────

export interface ChatLayout {
  kind: "chat";
  /** CSS grid-template-rows pair: composer height vs. feed height. */
  rowSplit: readonly [number, number];
  feedMaxWidth: number;
  composerMaxWidth: number;
  fontBody: number;
  fontHeading: number;
}

export function pickChatLayout(vp: Viewport): ChatLayout {
  const o = orientation(vp);
  // Vertical split — feed up top, composer at bottom. Composer should be
  // 1 part, feed should be φ parts. We project that onto Fibonacci pair (3,5)
  // (feed gets 5 / 8 of height, composer gets 3 / 8 of height capped to ~120).
  // For very tall screens we use (5,8) so the composer is even smaller.
  const rowSplit: readonly [number, number] = o === "landscape" ? [3, 5] : [3, 5];
  const ts = typeScale(vp.w < 480 ? 15 : 16, 5);
  return {
    kind: "chat",
    rowSplit,
    feedMaxWidth: readingWidth(vp),
    composerMaxWidth: readingWidth(vp),
    fontBody: ts[0],
    fontHeading: ts[2],
  };
}

export interface VideoLayout {
  kind: "video";
  /** Object-fit choice. Always "contain" so we never crop. */
  fit: "contain" | "cover";
  /** Optional letterbox colour. */
  letterbox: string;
}

export function pickVideoLayout(_vp: Viewport): VideoLayout {
  return { kind: "video", fit: "contain", letterbox: "#000" };
}

export interface GalleryLayout {
  kind: "gallery";
  /** Number of columns — picked from Fibonacci based on viewport width. */
  columns: number;
  /** Gap between cells in px (φ-stepped). */
  gap: number;
  /** Fibonacci-pair aspect ratio for thumbnails. */
  thumbRatio: readonly [number, number];
}

export function pickGalleryLayout(vp: Viewport): GalleryLayout {
  // Pick the largest Fibonacci ≤ floor(vp.w / 200). We cap at FIB[5]=8
  // because more than 8 columns turns into postage stamps.
  const target = Math.max(1, Math.floor(vp.w / 200));
  let cols = 1;
  for (const f of FIB) {
    if (f <= target && f <= 8) cols = f;
    else break;
  }
  const space = spaceScale(8, 4);
  return {
    kind: "gallery",
    columns: cols,
    gap: space[1],
    thumbRatio: [3, 5],
  };
}

export interface TextLayout {
  kind: "text";
  columnWidth: number;
  fontBody: number;
  fontH1: number;
  fontH2: number;
  fontH3: number;
  measure: number;
}

export function pickTextLayout(vp: Viewport): TextLayout {
  const ts = typeScale(vp.w < 480 ? 16 : 18, 6);
  return {
    kind: "text",
    columnWidth: readingWidth(vp),
    fontBody: ts[0],
    fontH3: ts[1],
    fontH2: ts[2],
    fontH1: ts[3],
    // ~ 2 alphabets per line — Bringhurst's 66-char ideal.
    measure: Math.round((readingWidth(vp) / ts[0]) * 0.55),
  };
}

export interface DataLayout {
  kind: "data";
  rowHeight: number;
  /** Suggested column-flex pattern based on Fibonacci. */
  columnFlex: readonly number[];
}

export function pickDataLayout(vp: Viewport): DataLayout {
  return {
    kind: "data",
    rowHeight: vp.w < 480 ? 36 : 44,
    columnFlex: [3, 5, 8, 5, 3], // φ-balanced default
  };
}

export interface SplitLayout {
  kind: "split";
  axis: "vertical" | "horizontal";
  /** Fibonacci pair — left/top vs right/bottom. */
  ratio: readonly [number, number];
}

/**
 * For input-on-left + output-on-right (or vice versa). Axis flips on
 * portrait screens so the content stays readable.
 */
export function pickSplitLayout(vp: Viewport): SplitLayout {
  const o = orientation(vp);
  const axis = o === "portrait" ? "horizontal" : "vertical";
  // Inputs are usually narrower than outputs — give 3 to inputs, 5 to outputs.
  const ratio = [3, 5] as const;
  return { kind: "split", axis, ratio };
}

// ─── Top-level entry ──────────────────────────────────────────────────

export type AnyLayout =
  | ChatLayout | VideoLayout | GalleryLayout | TextLayout | DataLayout | SplitLayout;

/**
 * Pick a layout for a given viewport + content type. Returns whichever
 * shape the content needs. Caller renders accordingly via Pulse.zone.*.
 */
export function pickLayout(vp: Viewport, type: ContentType): AnyLayout {
  switch (type) {
    case "chat":    return pickChatLayout(vp);
    case "video":   return pickVideoLayout(vp);
    case "gallery": return pickGalleryLayout(vp);
    case "text":    return pickTextLayout(vp);
    case "data":    return pickDataLayout(vp);
    case "split":   return pickSplitLayout(vp);
    case "canvas":  return pickChatLayout(vp); // canvas defaults to chat layout
  }
}

/** Read the live viewport. */
export function currentViewport(): Viewport {
  if (typeof window === "undefined") return { w: 1440, h: 900, dpr: 1 };
  return {
    w: Math.max(1, window.innerWidth),
    h: Math.max(1, window.innerHeight),
    dpr: window.devicePixelRatio || 1,
  };
}
